import getConfig from "next/config";
import { useContext, useEffect, useState } from "react";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { useUser } from "../../User/User.hook";

const { API_URL } = getConfig().publicRuntimeConfig;

const LEAD_ENDPOINT = `${API_URL}/api/1.0/sancor/guarantee-lead`;

const MENSAJE_ERROR = "Hubo un error. Volvé a intentarlo.";

/* El backend manda su propio mensaje; este cubre el caso en que el cuerpo no parsee. */
const MENSAJE_OK = "Recibimos tus datos. Un agente de Sancor Seguros te va a contactar.";

/*
 * La cotización es el espejo de `app/Services/SancorGuaranteeQuote.php` en IC2 (INF-604):
 * el backend recalcula el premio al recibir el lead y guarda el suyo, así que cualquier
 * divergencia con estos números hace que la pantalla muestre un importe distinto del que
 * Sancor después tiene que honrar. Los valores se toman de ahí, no se re-derivan.
 */

/**
 * `monedas.id`, tal como los mapea `Property/FichaStructData.tsx`.
 *
 * Se comparan como string a propósito: el tipo `Currency` declara `id?: string`, pero el
 * search-service manda el **número** (medido contra QA el 2026-08-24: `currency: {rate:
 * 7270.1, name: 'Gs.', id: 3}`). `FichaStructData` no lo nota porque lo usa como clave de
 * objeto, que coacciona sola; un `===` contra `"3"` daría siempre `null` y el widget no
 * aparecería nunca.
 */
const CURRENCY_PYG = "3";
const CURRENCY_USD = "1";

const MULTIPLIER = 10;
const RATE = 0.03;

/** Piso del premio total, por moneda. */
const MINIMUM_PREMIUM = { PYG: 400000, USD: 70 };

/**
 * Techo de plausibilidad del canon: cubre los avisos con un precio de VENTA cargado en el
 * campo de alquiler, que sin el corte mostrarían un premio de siete cifras.
 *
 * El de guaraníes NO se deriva del de dólares — es el mismo techo llevado con el arbitraje
 * de `monedas` (~6.000), y lo fija el backend. No se recalcula acá.
 */
const MAX_PLAUSIBLE_CANON = { PYG: 300000000, USD: 50000 };

type CurrencyCode = "PYG" | "USD";

const currencyCodeOf = (currencyId?: string | number): CurrencyCode | null => {
	const id = String(currencyId);

	if (id === CURRENCY_PYG) return "PYG";
	if (id === CURRENCY_USD) return "USD";
	return null;
};

/**
 * `canon x 10 x 3%`, nunca por debajo del piso de su moneda, redondeado a la unidad con la
 * que se cobra: el guaraní no tiene subunidad, el dólar conserva centavos.
 */
const sancorPremiumFor = (canon: number, currencyCode: CurrencyCode): number => {
	const premium = Math.max(canon * MULTIPLIER * RATE, MINIMUM_PREMIUM[currencyCode]);

	return currencyCode === "USD" ? Math.round(premium * 100) / 100 : Math.round(premium);
};

/**
 * La cotización del aviso, o `null` si el aviso no admite garantía.
 *
 * El país y el tipo de operación los filtra el montaje (`Financing/web.tsx`), igual que
 * con los demás partners; acá quedan las reglas que dependen del precio.
 */
const sancorQuoteFor = (price?: Price) => {
	if (!price || price.hidePrice) return null;

	const currencyCode = currencyCodeOf(price.currency?.id);

	if (currencyCode === null) return null;

	const canon = Math.trunc(Number(price.amount));

	// `Number.isFinite` y no `!canon`: un `NaN` pasaría el `<= 0` de abajo.
	if (!Number.isFinite(canon) || canon <= 0) return null;

	// `>=` y no `>`, como el backend: un alquiler de exactamente el techo es un precio de
	// venta mal cargado, no un canon.
	if (canon >= MAX_PLAUSIBLE_CANON[currencyCode]) return null;

	return { premium: sancorPremiumFor(canon, currencyCode) };
};

type SubmitStatus = "idle" | "loading" | "success" | "error";

/**
 * Texto mostrable a partir del `message` de una respuesta de error. Un string se muestra tal
 * cual; el `MessageBag` del 400 se aplana a sus mensajes; cualquier otra cosa cae al genérico.
 */
const mensajeDeError = (message: unknown): string => {
	if (typeof message === "string") return message;

	if (message && typeof message === "object") {
		const textos = Object.values(message)
			.flat()
			.filter(texto => typeof texto === "string");

		if (textos.length > 0) return textos.join(" ");
	}

	return MENSAJE_ERROR;
};

/**
 * Nombre y apellido del logueado. `firstName` / `lastName` son los campos propios, pero
 * pueden venir vacíos con `name` cargado (el alta social guarda el nombre entero), y en ese
 * caso el widget quedaría sin prellenar: por eso el fallback parte `name` por el primer
 * espacio. Es el mismo `me` que el `ContactModal` de Financing usa para su form.
 */
const nombreDe = (me: User): { first: string; last: string } => {
	if (me.firstName || me.lastName) return { first: me.firstName ?? "", last: me.lastName ?? "" };

	const [first = "", ...resto] = (me.name ?? "").trim().split(/\s+/);

	return { first, last: resto.join(" ") };
};

const CAMPOS_VACIOS = {
	first_name: "",
	last_name: "",
	document: "",
	phone: "",
	email: "",
};

export const useSancorGuarantee = ({ propertyId, price }: { propertyId: string; price?: Price }) => {
	const { main_domain } = useContext(ConfigStateContext);
	const { user } = useUser();
	const me = user?.data?.me;
	const [form, setForm] = useState(CAMPOS_VACIOS);
	const [status, setStatus] = useState<SubmitStatus>("idle");
	const [message, setMessage] = useState("");

	const quote = sancorQuoteFor(price);

	/**
	 * Prellenado con los datos del logueado (INF-645). `user.data` ES el `UserStateContext`
	 * —no una query—, así que esto no agrega request ni depende de un fetch que llegue.
	 *
	 * Sólo se rellena lo que está vacío: `me` puede llegar después del primer render, y
	 * pisar un campo ya tipeado es peor que no prellenar. `document` (la C.I.) queda afuera
	 * porque el usuario no la tiene: ni el tipo `User` ni el query `ME_HEADER` la traen.
	 */
	useEffect(() => {
		if (!me) return;

		const { first, last } = nombreDe(me);

		setForm(previous => ({
			...previous,
			first_name: previous.first_name || first,
			last_name: previous.last_name || last,
			email: previous.email || me.email || "",
			phone: previous.phone || me.phone || "",
		}));
	}, [me?.firstName, me?.lastName, me?.name, me?.email, me?.phone]);

	const handleChange = event => {
		const { name, value } = event.target;

		setForm(previous => ({ ...previous, [name]: value }));
	};

	const handleSubmit = async event => {
		event.preventDefault();

		// El submit implícito (Enter en un campo) no lo frena el `loading` de antd, y el
		// dedupe del backend hace select-y-después-insert sin lock: dos POST casi
		// simultáneos pueden no verse entre sí y dejar DOS leads del mismo aviso.
		if (status === "loading") return;

		setStatus("loading");

		try {
			const response = await fetch(LEAD_ENDPOINT, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					// Obligatorio: `countryCheck` lo traduce a `app.country_id`, y sin eso
					// el scope de país de `Property` no encuentra nunca el aviso.
					"x-origin": main_domain,
				},
				body: JSON.stringify({ property_id: propertyId, ...form }),
			});

			const body = await response.json().catch(() => null);

			// El `201` es lo único que confirma que el lead quedó guardado — y no garantiza
			// fila nueva: el dedupe de 24h del backend puede haber pisado una anterior.
			//
			// Acá NO va evento de conversión, a propósito (INF-645, decisión de Santiago del
			// 2026-08-26): el lead de Sancor lo mide el backend, que lo guarda y lo sube al
			// Sheet cada hora (INF-604). El `ContactModal` de Financing sí le manda un
			// `CompleteRegistration` al pixel; este widget no lo replica.
			if (response.status === 201) {
				setStatus("success");
				// Hoy el 201 siempre trae un string, pero el chequeo va igual: es un
				// carácter, y lo que evita es que un cambio del otro lado deje la ficha en
				// blanco (el porqué está unas líneas más abajo).
				setMessage(typeof body?.message === "string" ? body.message : MENSAJE_OK);

				return;
			}

			// Los errores se distinguen por status: `400` es la validación de los campos (el
			// Handler de IC2 renderiza así toda `ValidationException`, no como 422), `422` es
			// el aviso que no admite garantía y `404` el aviso inexistente o el kill-switch
			// apagado.
			//
			// ⚠️ El `message` del 400 NO es un string: `Handler::render` manda ahí el
			// `MessageBag` del validador, que llega como `{campo: ["error"]}`. Ese objeto
			// como hijo de React tira «Objects are not valid as a React child», y sin error
			// boundary en el repo eso desmonta la ficha entera — pantalla en blanco, no un
			// widget roto. Por eso nunca se muestra el objeto crudo, pero sí sus mensajes:
			// el 400 sigue siendo alcanzable (la regla `email` de PHP es más estricta que la
			// del browser) y decir cuál campo falló es la diferencia entre poder corregirlo y
			// no poder.
			setStatus("error");
			setMessage(mensajeDeError(body?.message));
		} catch {
			setStatus("error");
			setMessage(MENSAJE_ERROR);
		}
	};

	return {
		quote,
		form,
		handleChange,
		handleSubmit,
		status,
		message,
	};
};
